import config from "../config";

const CACHE_TTL = {
  STATIC: 60 * 60, // 1 hour for static content (in seconds)
  SEMI_DYNAMIC: 15 * 60, // 15 minutes for semi-dynamic content
  DYNAMIC: 5 * 60, // 5 minutes for dynamic content
};

function parseGraphQLQuery(query) {
  // Extract meaningful parameters from GraphQL queries
  const params = [];
  
  // Extract collection type and determine if it's single or list
  let contentType = "unknown";
  let queryMode = "list";
  
  if (query.includes("blogArticleCollection")) {
    contentType = "blog";
    queryMode = "list";
  } else if (query.includes("blogArticle(")) {
    contentType = "blog";
    queryMode = "single";
  } else if (query.includes("contentTypeLocationCollection")) {
    contentType = "location";
    queryMode = "list";
  } else if (query.includes("solutionCollection")) {
    contentType = "solution";
    queryMode = "list";
  } else if (query.includes("optionCollection")) {
    contentType = "option";
    queryMode = "list";
  } else if (query.includes("optionsCollection")) {
    contentType = "option";
    queryMode = "list";
  } else if (query.includes("seoPageSettingsCollection")) {
    contentType = "seo";
    queryMode = "list";
  } else if (query.includes("teamMemberCollection")) {
    contentType = "team";
    queryMode = "list";
  } else if (query.includes("faqSectionCollection")) {
    contentType = "faq";
    queryMode = "list";
  } else if (query.includes("galleryCollection")) {
    contentType = "gallery";
    queryMode = "single";
  } else if (query.includes("meetOurClientsCollection")) {
    contentType = "clients";
    queryMode = "single";
  } else if (query.includes("calloutSectionCollection")) {
    contentType = "callout";
    queryMode = "single";
  } else if (query.includes("featuredOptionsCollection")) {
    contentType = "featured";
    queryMode = "list";
  }
  
  // Extract where clause parameters
  const whereMatch = query.match(/where:\s*\{([^}]+)\}/);
  if (whereMatch) {
    const whereClause = whereMatch[1];
    
    // Extract slug
    const slugMatch = whereClause.match(/slug:\s*"([^"]+)"/);
    if (slugMatch) {
      params.push(`slug-${slugMatch[1]}`);
      if (contentType !== "unknown") queryMode = "single";
    }
    
    // Extract pageSlug
    const pageSlugMatch = whereClause.match(/pageSlug:\s*"([^"]+)"/);
    if (pageSlugMatch) {
      params.push(`pageSlug-${pageSlugMatch[1]}`);
      queryMode = "single";
    }
  }
  
  // Extract ID for preview queries
  const idMatch = query.match(/blogArticle\(id:\s*"([^"]+)"/);
  if (idMatch) {
    params.push(`id-${idMatch[1]}`);
    queryMode = "single";
  }
  
  // Extract pagination parameters
  const limitMatch = query.match(/limit:\s*(\d+)/);
  if (limitMatch) params.push(`limit-${limitMatch[1]}`);
  
  const skipMatch = query.match(/skip:\s*(\d+)/);
  if (skipMatch) params.push(`skip-${skipMatch[1]}`);
  
  // Extract order parameters
  const orderMatch = query.match(/order:\s*([\w_]+)/);
  if (orderMatch) params.push(`order-${orderMatch[1]}`);
  
  return { contentType, queryMode, params };
}

function generateCacheKey(query, preview) {
  const { contentType, queryMode, params } = parseGraphQLQuery(query);
  
  // Build human-readable cache key
  const keyParts = [
    "contentful",
    preview ? "preview" : "published",
    contentType,
    queryMode
  ];
  
  // Add parameters in sorted order for consistency
  if (params.length > 0) {
    keyParts.push(...params.sort());
  }
  
  return keyParts.join(":");
}

function getTTLForQuery(query) {
  const staticContentTypes = ["contentTypeTeamMember", "contentTypeSolution", "contentTypeCalloutSection", "contentTypeSeoSetting"];
  const dynamicContentTypes = ["contentTypeLocationReview", "contentTypeAppointment"];
  
  if (staticContentTypes.some(type => query.includes(type))) {
    return CACHE_TTL.STATIC;
  }
  
  if (dynamicContentTypes.some(type => query.includes(type))) {
    return CACHE_TTL.DYNAMIC;
  }
  
  return CACHE_TTL.SEMI_DYNAMIC;
}

class MemoryCache {
  constructor() {
    this.cache = new Map();
    this.keyCollisions = new Map(); // Track potential hash collisions
    this.stats = {
      hits: 0,
      misses: 0,
      collisions: 0,
      startTime: Date.now()
    };
  }

  async get(key) {
    if (this.cache.has(key)) {
      const cached = this.cache.get(key);
      const now = Date.now();
      
      if (now < cached.expiresAt) {
        this.stats.hits++;
        console.log(`Cache hit: ${key}`);
        return cached.data;
      } else {
        this.cache.delete(key);
      }
    }
    return null;
  }

  async set(key, data, ttlSeconds) {
    const now = Date.now();
    
    // Check for potential collisions
    if (this.cache.has(key)) {
      const existing = this.cache.get(key);
      if (JSON.stringify(existing.data) !== JSON.stringify(data)) {
        this.stats.collisions++;
        console.warn(`Cache key collision detected for key: ${key}`);
        this.keyCollisions.set(key, (this.keyCollisions.get(key) || 0) + 1);
      }
    }
    
    this.cache.set(key, {
      data,
      expiresAt: now + (ttlSeconds * 1000),
      createdAt: now,
      keyHash: key // Store the key for debugging
    });
    
    this.stats.misses++;
    console.log(`Cache miss: ${key} (TTL: ${ttlSeconds}s)`);
    
    if (this.cache.size > 100) {
      this.cleanup();
    }
  }

  cleanup() {
    const now = Date.now();
    for (const [key, value] of this.cache.entries()) {
      if (now > value.expiresAt) {
        this.cache.delete(key);
      }
    }
  }

  getStats() {
    const now = Date.now();
    const uptime = now - this.stats.startTime;
    const total = this.stats.hits + this.stats.misses;
    const hitRate = total > 0 ? (this.stats.hits / total * 100).toFixed(2) : 0;
    
    return {
      type: "memory-dev",
      hits: this.stats.hits,
      misses: this.stats.misses,
      collisions: this.stats.collisions,
      total,
      hitRate: `${hitRate}%`,
      cacheSize: this.cache.size,
      uptime: `${Math.round(uptime / 1000)}s`,
      collisionKeys: Array.from(this.keyCollisions.entries())
    };
  }
}

// Use memory cache for development and build optimization only
const cacheAdapter = new MemoryCache();

export async function fetchContent(query, preview = false) {
  // Validate query before processing
  if (!query || typeof query !== "string" || query.trim().length === 0) {
    console.error("Invalid query provided to fetchContent:", query);
    return null;
  }
  
  const cacheKey = generateCacheKey(query, preview);
  
  const cached = await cacheAdapter.get(cacheKey);
  if (cached) {
    return cached;
  }
  
  try {
    const response = await fetch(
      `https://graphql.contentful.com/content/v1/spaces/${config.CONTENTFUL_SPACE_ID}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${
            preview
              ? config.CONTENTFUL_PREVIEW_TOKEN
              : config.CONTENTFUL_ACCESS_TOKEN
          }`
        },
        body: JSON.stringify({ query })
      }
    );

    const data = await response.json();
    
    const ttl = getTTLForQuery(query);
    await cacheAdapter.set(cacheKey, data, ttl);

    return data;
  } catch (e) {
    console.log(e);
    console.log(`There was a problem fetching content with query ${query}`);
    return;
  }
}

export function getCacheStats() {
  return cacheAdapter.getStats();
}
